import React, { useMemo } from 'react';
import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  VStack,
  HStack,
  Text,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { DrawerContainer } from 'src/components/molecules/DrawerContainer';
import { CampaignList, Campaign } from '../CampaignList';
import { CampaignSelector } from '../CampaignSelector';
import { GroupNameInput } from '../GroupNameInput';

export type CampaignGroup = {
  id: string;
  groupName: string;
  campaigns: number;
  created: string;
  campaignList?: Campaign[];
};

type ViewGroupDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  group: CampaignGroup | null;
  mode: 'view' | 'edit';
  onEdit: () => void;
  onSave: () => void;
  onDelete: () => void;
  editGroupName: string;
  onEditGroupNameChange: (name: string) => void;
  editGroupCampaigns: Campaign[];
  onRemoveCampaign: (campaignId: string) => void;
  onAddCampaigns: () => void;
  isAddingCampaigns: boolean;
  allCampaigns: Campaign[];
  onToggleCampaignToAdd: (campaignId: string) => void;
  addCampaignsSearchQuery: string;
  onAddCampaignsSearchChange: (query: string) => void;
};

export const ViewGroupDrawer: React.FC<ViewGroupDrawerProps> = ({
  isOpen,
  onClose,
  group,
  mode,
  onEdit,
  onSave,
  onDelete,
  editGroupName,
  onEditGroupNameChange,
  editGroupCampaigns,
  onRemoveCampaign,
  onAddCampaigns,
  isAddingCampaigns,
  allCampaigns,
  onToggleCampaignToAdd,
  addCampaignsSearchQuery,
  onAddCampaignsSearchChange,
}) => {
  const isCampaignChecked = (campaignId: string) => {
    return editGroupCampaigns.some((c) => c.id === campaignId);
  };

  const selectedCampaignIds = useMemo(
    () => new Set(editGroupCampaigns.map((campaign) => campaign.id)),
    [editGroupCampaigns],
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContainer>
        <DrawerCloseButton />
        <DrawerHeader pb={4} pt={12}>
          {mode === 'view' ? (
            <>
              <Text
                fontFamily="marketingPage"
                fontWeight="bold"
                fontSize="32px"
                color="content.title">
                {group?.groupName}
              </Text>
              <Text
                fontSize="sm"
                color="content.muted"
                mt={2}
                fontWeight="normal">
                {group?.campaigns} campaigns in this group
              </Text>
            </>
          ) : (
            <Box display="flex" flexDirection="column" height="100%">
              <HStack justify="space-between" align="center" flex={1} mb={2}>
                <Text
                  fontFamily="marketingPage"
                  fontWeight="bold"
                  fontSize="32px"
                  color="content.title">
                  {group?.groupName}
                </Text>
                <Button
                  bg="surface.panel"
                  color="background.red.500"
                  border="1px solid"
                  borderColor="background.red.500"
                  size="sm"
                  px={4}
                  py={2}
                  marginY="auto"
                  borderRadius="md"
                  fontWeight="bold"
                  buttonName="delete-group"
                  fontFamily="marketingPage"
                  onClick={onDelete}
                  _hover={{
                    bg: 'surface.errorHover',
                    borderColor: 'icon.red',
                  }}>
                  Delete
                </Button>
              </HStack>
              <Text fontSize="sm" color="content.muted" fontWeight="normal">
                {editGroupCampaigns.length} campaigns in this group
              </Text>
            </Box>
          )}
        </DrawerHeader>

        <DrawerBody p={6}>
          {mode === 'view' ? (
            <VStack align="stretch" spacing={4}>
              <Text
                fontFamily="marketingPage"
                fontWeight={500}
                fontSize="16px"
                color="content.title"
                mb={3}>
                Campaigns
              </Text>

              {group?.campaignList && (
                <CampaignList campaigns={group.campaignList} />
              )}
            </VStack>
          ) : (
            <VStack align="stretch" spacing={6}>
              {/* Group Name Input */}
              <GroupNameInput
                label="Group Name"
                value={editGroupName}
                onChange={onEditGroupNameChange}
              />

              {/* Campaigns Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={3}>
                  <Text
                    fontFamily="marketingPage"
                    fontWeight={500}
                    fontSize="16px"
                    color="content.title">
                    Campaigns
                  </Text>
                  {!isAddingCampaigns && (
                    <Button
                      variant="ghost"
                      size="sm"
                      px={3}
                      py={1}
                      fontWeight="normal"
                      fontSize="sm"
                      color="content.muted"
                      buttonName="add-campaigns"
                      fontFamily="marketingPage"
                      onClick={onAddCampaigns}
                      _hover={{ bg: 'sidebar.itemHoverBg' }}>
                      + Add Campaigns
                    </Button>
                  )}
                </HStack>

                {isAddingCampaigns ? (
                  <CampaignSelector
                    campaigns={allCampaigns}
                    selectedCampaignIds={selectedCampaignIds}
                    searchQuery={addCampaignsSearchQuery}
                    onSearchChange={onAddCampaignsSearchChange}
                    onToggleCampaign={onToggleCampaignToAdd}
                    isChecked={isCampaignChecked}
                  />
                ) : (
                  <CampaignList
                    campaigns={editGroupCampaigns}
                    onRemove={onRemoveCampaign}
                    showRemoveButton
                  />
                )}
              </Box>
            </VStack>
          )}
        </DrawerBody>

        <Box p={6} display="flex" gap={4}>
          {mode === 'view' ? (
            <>
              <Button
                bg="surface.sidebarMuted"
                color="content.body"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="cancel-group-drawer"
                fontFamily="marketingPage"
                onClick={onClose}
                flex={1}
                _hover={{ bg: 'sidebar.itemHoverBg' }}>
                Cancel
              </Button>
              <Button
                bg="icon.green"
                color="white"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="edit-group-drawer"
                fontFamily="marketingPage"
                onClick={onEdit}
                flex={1}
                _hover={{ bg: 'green.600' }}>
                Edit Group
              </Button>
            </>
          ) : (
            <>
              <Button
                bg="surface.sidebarMuted"
                color="content.body"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="cancel-edit-group"
                fontFamily="marketingPage"
                onClick={onClose}
                flex={1}
                _hover={{ bg: 'sidebar.itemHoverBg' }}>
                Cancel
              </Button>
              <Button
                bg="icon.green"
                color="white"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="save-changes"
                fontFamily="marketingPage"
                onClick={onSave}
                flex={1}
                _hover={{ bg: 'green.600' }}>
                Save Changes
              </Button>
            </>
          )}
        </Box>
      </DrawerContainer>
    </Drawer>
  );
};
